/**
 * The 1:1 map between ATLAS `metadata.productStatus` and Asana's
 * `PD PROJECT STAGE` custom field (#924).
 *
 * Both vocabularies were extended on 2026-09-16 so every value has exactly one
 * counterpart: ATLAS gained `NEW` and `NPSO Phase`, Asana gained `Production`,
 * `Rework` and `Discontinued`. Before that the map was lossy in both directions
 * — `1 - Production` and `2 - PLS` collapsed onto one Asana option, so a value
 * could not survive a round trip.
 *
 * This is the single source of truth for the correspondence. The sync reads and
 * writes through it, and the UI can use it to show a PM which Asana stage a
 * status corresponds to. That display need is why the map lives here rather
 * than baked into the stored option strings: `product_metadata_fields.options`
 * is a bare `string[]` with no label/value split, so the option text IS the
 * stored value, and putting "(LIVE)" in it would be a rename across every
 * product document, every version document, and the stored preferences keyed by
 * the value.
 *
 * GIDs are stable infrastructure for the single Asana workspace
 * (13228076834303) that dev and production share, so they live in code rather
 * than env for the same reason `asanaPortfolios.ts` does: the map never varies
 * by environment, and a typo is a build error here instead of a silent runtime
 * miss. Verified live 2026-09-16.
 */
/** The `PD PROJECT STAGE` multi_enum, attached to the eight `<Brand> - HQ` folders. */
export const PD_PROJECT_STAGE_FIELD_GID = '1216670842320750';
/**
 * Every ATLAS `metadata.productStatus` value. Order follows the dropdown.
 *
 * Named "lifecycle" because `ProductStatus` is already taken in
 * `product.types.ts` for the DOCUMENT status (`draft`/`published`/`deprecated`),
 * which is a different axis entirely. Conflating the two has bitten before —
 * keep them apart.
 */
export const PRODUCT_LIFECYCLE_STATUSES = [
    '0 - Rework',
    '1 - Production',
    '2 - PLS',
    '3 - Development',
    '4 - Concept',
    '5 - Back Burner',
    '6 - Launched',
    '7 - Discontinued',
    'NEW',
    'NPSO Phase',
];
/** Every `PD PROJECT STAGE` option, in the order Asana lists them. */
export const ASANA_STAGES = [
    'NEW',
    'NPC Phase',
    'Development Phase',
    'NPSO Phase',
    'Trial Production Phase / PLS',
    'Production',
    'LIVE',
    'ZOMBIE',
    'Rework',
    'Discontinued',
];
/** Enum-option GID per stage. A multi_enum write takes an ARRAY of these. */
export const ASANA_STAGE_GIDS = {
    NEW: '1217188958483666',
    'NPC Phase': '1216670842320751',
    'Development Phase': '1217119932593316',
    'NPSO Phase': '1217119932593317',
    'Trial Production Phase / PLS': '1217119932593318',
    Production: '1218551419275520',
    LIVE: '1216670842320752',
    ZOMBIE: '1216670842320753',
    Rework: '1218551590309573',
    Discontinued: '1218551419138365',
};
/**
 * ATLAS status -> Asana stage. Exhaustive by type, so adding a `ProductLifecycleStatus`
 * without deciding its stage is a build error rather than a silent skip.
 */
export const ASANA_STAGE_FOR_STATUS = {
    NEW: 'NEW',
    '0 - Rework': 'Rework',
    '4 - Concept': 'NPC Phase',
    '3 - Development': 'Development Phase',
    'NPSO Phase': 'NPSO Phase',
    '2 - PLS': 'Trial Production Phase / PLS',
    '1 - Production': 'Production',
    '6 - Launched': 'LIVE',
    '5 - Back Burner': 'ZOMBIE',
    '7 - Discontinued': 'Discontinued',
};
/** Asana stage -> ATLAS status. Derived, so the two can never disagree. */
export const STATUS_FOR_ASANA_STAGE = Object.fromEntries(Object.entries(ASANA_STAGE_FOR_STATUS).map(([status, stage]) => [stage, status]));
const normalise = (value) => typeof value === 'string' ? value.trim().replace(/\s+/g, ' ') : '';
/** Narrow an arbitrary value to a known ATLAS status. */
export const isProductLifecycleStatus = (value) => PRODUCT_LIFECYCLE_STATUSES.includes(normalise(value));
/** Narrow an arbitrary value to a known Asana stage. */
export const isAsanaStage = (value) => ASANA_STAGES.includes(normalise(value));
/**
 * Asana stage for an ATLAS status, or `undefined` for a value outside the
 * vocabulary. Callers must treat `undefined` as "leave Asana alone" rather than
 * clearing the field — an unknown status is a reason to do nothing, not to
 * erase what a PM set.
 */
export const asanaStageForStatus = (status) => isProductLifecycleStatus(status) ? ASANA_STAGE_FOR_STATUS[normalise(status)] : undefined;
/** ATLAS status for an Asana stage, or `undefined` outside the vocabulary. */
export const statusForAsanaStage = (stage) => isAsanaStage(stage) ? STATUS_FOR_ASANA_STAGE[normalise(stage)] : undefined;
/**
 * The stage a portfolio's field value represents, for the sync's comparison.
 *
 * ⚠️ `PD PROJECT STAGE` is a **multi_enum**: a PM can tick several stages at
 * once, and ~1,080 portfolios carry a value today. A single-valued
 * `productStatus` cannot represent two, so anything other than exactly one
 * recognised stage is reported as ambiguous and never pulled into ATLAS. The
 * push side still overwrites it with the single correct value, which is what
 * resolves the ambiguity.
 */
export const readStage = (values) => {
    const known = (values ?? [])
        .map((v) => normalise(v?.name))
        .filter((n) => isAsanaStage(n));
    if (known.length === 1)
        return { stage: known[0], ambiguous: false };
    return { stage: undefined, ambiguous: (values ?? []).length > 0 };
};
//# sourceMappingURL=asana-product-status.js.map